/**
 * The only Zustand store in the app. Server state belongs to TanStack Query;
 * this holds nothing but the session.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { TraderProfile } from '@shared/types';

interface AuthState {
  accessToken: string | null;
  trader: TraderProfile | null;
  /** Unix ms at which the access token stops being usable. */
  expiresAt: number | null;

  isAuthenticated: () => boolean;
  signIn: (
    accessToken: string,
    expiresInSeconds: number,
    trader: TraderProfile | null,
  ) => void;
  setTrader: (trader: TraderProfile) => void;
  signOut: () => void;
}

const MS_PER_SECOND = 1000;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      trader: null,
      expiresAt: null,

      isAuthenticated: () => {
        const { accessToken, expiresAt } = get();
        if (!accessToken) return false;
        if (expiresAt !== null && Date.now() >= expiresAt) return false;
        return true;
      },

      signIn: (accessToken, expiresInSeconds, trader) =>
        set({
          accessToken,
          trader,
          expiresAt: Date.now() + expiresInSeconds * MS_PER_SECOND,
        }),

      setTrader: (trader) => set({ trader }),

      signOut: () => set({ accessToken: null, trader: null, expiresAt: null }),
    }),
    {
      name: 'exchange.trader.auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        trader: state.trader,
        expiresAt: state.expiresAt,
      }),
    },
  ),
);

/** Read the token outside React (axios interceptors, EventSource setup). */
export function getAccessToken(): string | null {
  return useAuthStore.getState().accessToken;
}

export function signOutNow(): void {
  useAuthStore.getState().signOut();
}
